import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Container, Alert, Divider } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  errorComponent?: 'minimal' | 'detailed' | 'custom';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors in its child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = (): void => {
    // Reset the error boundary state and trigger any onReset handler provided
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, errorComponent = 'detailed' } = this.props;

    if (!hasError) {
      return children;
    }

    // If a custom fallback is provided, use it
    if (fallback) {
      return fallback;
    }

    // Minimal error component for smaller UI sections
    if (errorComponent === 'minimal') {
      return (
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={this.handleReset}>
              Retry
            </Button>
          }
        >
          Something went wrong. Please try again.
        </Alert>
      );
    }

    // Detailed error component with additional information
    if (errorComponent === 'detailed') {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              borderRadius: 2,
              border: '1px solid #f5c6cb'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h5" color="error" fontWeight="bold">
                Something went wrong
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              We apologize for the inconvenience. The application encountered an unexpected error.
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleReset}
              sx={{ mb: 3 }}
            >
              Try Again
            </Button>
            
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => window.location.reload()}
              sx={{ ml: 2, mb: 3 }}
            >
              Reload Page
            </Button>
            
            {process.env.NODE_ENV === 'development' && (
              <>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Error Details (Development Only):
                </Typography>
                
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error?.toString()}
                </Alert>
                
                {errorInfo && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      borderRadius: 1, 
                      bgcolor: 'grey.100',
                      overflowX: 'auto',
                      fontSize: '0.875rem',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    <code>
                      {errorInfo.componentStack}
                    </code>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Container>
      );
    }

    // Default fallback UI if errorComponent is not recognized
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          An error occurred. Please try again.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={this.handleReset}
          sx={{ mt: 2 }}
        >
          Reset
        </Button>
      </Box>
    );
  }
}

export default ErrorBoundary; 